import type { TabDef } from "./types";
import { ordenarPorNome } from "./ordem";

/** Todas as abas do sistema. A ordem define o menu lateral. */
export const TABS: TabDef[] = [
  {
    key: "atividades",
    label: "Atividades",
    icon: "list",
    resource: "atividades",
    subtitle:
      "Atividades (Ordens de Serviço) em Kanban — arraste o cartão para mudar de etapa; dois cliques abrem o checkpoint",
    compact: true,
    fields: [
      { name: "cliente", label: "Nome do cliente", type: "text", required: true },
      { name: "cidade", label: "Cidade", type: "text" },
      { name: "colaborador_id", label: "Responsável técnico", type: "select", ref: "colaboradores" },
      { name: "nome", label: "Atividade", type: "text", required: true },
      { name: "valor_faturado", label: "Valor faturado", type: "currency" },
      { name: "valor_custo", label: "Valor de custo", type: "currency" },
      { name: "prazo_execucao_dias", label: "Prazo de execução (d)", type: "number" },
      { name: "data_retorno_maximo", label: "Prazo máx. p/ retorno", type: "date" },
      { name: "data_cliente", label: "Data definida c/ cliente", type: "date" },
      { name: "situacao", label: "Situação", type: "status", computed: true },
      {
        name: "status",
        label: "Status",
        type: "status",
        options: ["Pendente", "Aguardando cliente", "Agendada", "Em andamento", "Executada", "Atrasada"],
      },
    ],
  },
  {
    key: "satisfacao",
    label: "Satisfação",
    icon: "smile",
    resource: "satisfacao",
    subtitle: "Nível de satisfação do cliente por obra",
    fields: [
      { name: "obra_id", label: "Obra", type: "select", ref: "obras" },
      { name: "colaborador_id", label: "Responsável", type: "select", ref: "colaboradores" },
      { name: "cliente", label: "Cliente", type: "text" },
      { name: "nota", label: "Nota (0–10)", type: "number" },
      {
        name: "nivel",
        label: "Nível",
        type: "status",
        options: ["Muito satisfeito", "Satisfeito", "Neutro", "Insatisfeito", "Muito insatisfeito"],
      },
      { name: "data", label: "Data", type: "date" },
      { name: "comentario", label: "Comentário", type: "textarea" },
    ],
  },
  {
    key: "retrabalho",
    label: "Retrabalho",
    icon: "repeat",
    resource: "retrabalho",
    subtitle: "Índices de retrabalho por colaborador",
    fields: [
      { name: "colaborador_id", label: "Responsável", type: "select", ref: "colaboradores", required: true },
      { name: "obra_id", label: "Obra", type: "select", ref: "obras" },
      { name: "descricao", label: "Descrição", type: "text" },
      { name: "quantidade", label: "Quantidade", type: "number" },
      {
        name: "motivo",
        label: "Motivo",
        type: "select",
        options: [
          "Falha de execução",
          "Especificação errada",
          "Material defeituoso",
          "Medição incorreta",
          "Erro de projeto",
          "Outro",
        ],
      },
      { name: "custo", label: "Custo", type: "currency" },
      { name: "data", label: "Data", type: "date" },
      { name: "status", label: "Status", type: "status", options: ["Aberto", "Em correção", "Resolvido"] },
    ],
  },
  {
    // CADASTRO da frota (mesma tabela do banco compartilhado). As MANUTENÇÕES
    // saíram daqui: elas têm tabela própria (`manutencoes_veiculo`) e são
    // registradas na aba "Manutenção de Veículos" da Gestão da Operação —
    // um veículo tem muitas revisões, e nesta linha só cabia uma.
    key: "veiculos",
    label: "Veículos",
    icon: "truck",
    resource: "veiculos",
    subtitle:
      "Cadastro da frota. As revisões e serviços ficam na aba 'Manutenção de Veículos', na Gestão da Operação.",
    // O veículo é identificado pela PLACA: é por ela que a lista fica em ordem.
    ordenarPor: "placa",
    fields: [
      { name: "placa", label: "Placa", type: "text" },
      { name: "modelo", label: "Modelo", type: "text" },
      { name: "marca", label: "Marca", type: "text" },
      { name: "ano", label: "Ano", type: "number" },
      { name: "colaborador_id", label: "Responsável", type: "select", ref: "colaboradores" },
      { name: "km_atual", label: "KM atual", type: "number" },
      {
        name: "status",
        label: "Situação do veículo",
        type: "status",
        options: ["Ativo", "Em manutenção", "Inativo", "Vendido"],
      },
    ],
  },
  {
    key: "equipamentos",
    label: "Equipamentos de Ensaio",
    icon: "gauge",
    resource: "equipamentos",
    subtitle: "Autorização operacional e controle de calibração",
    ordenarPor: "nome",
    fields: [
      { name: "nome", label: "Equipamento", type: "text", required: true },
      { name: "numero_serie", label: "Nº de série", type: "text" },
      {
        name: "tipo",
        label: "Tipo",
        type: "select",
        options: [
          "Isolação",
          "Resistência de contato",
          "Relação de espiras (TTR)",
          "Fator de potência",
          "Rigidez dielétrica",
          "Termografia",
          "Aterramento",
          "Corrente",
          "Multímetro",
          "Calibração",
          "Oscilografia",
          "Outro",
        ],
      },
      { name: "colaborador_id", label: "Responsável", type: "select", ref: "colaboradores" },
      { name: "data_calibracao", label: "Calibração", type: "date" },
      { name: "data_validade", label: "Validade", type: "date", required: true },
      { name: "situacao", label: "Situação", type: "status", computed: true },
      {
        name: "autorizacao",
        label: "Autorização",
        type: "status",
        options: ["Autorizado", "Bloqueado", "Em calibração"],
      },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  /**
   * SEPARAÇÃO DE MATERIAL — a OS pedindo o que precisa para ser executada.
   *
   * Aba de tela própria (`custom`): não é a listagem de uma tabela daqui, e sim
   * a mesma OS conversando com dois outros sistemas — o Almoxarifado (separar
   * os insumos) e o Laboratório (receber as amostras). Por isso não tem
   * `fields`: quem desenha é SeparacaoMaterialView.
   */
  {
    key: "separacao_material",
    label: "Separação de Material",
    icon: "truck",
    resource: "atividades",
    custom: true,
    subtitle:
      "Peça a separação dos insumos ao Almoxarifado e envie o material da OS ao Laboratório",
    fields: [],
  },
  {
    key: "colaboradores",
    label: "Equipe",
    icon: "users",
    resource: "colaboradores",
    subtitle: "Equipe técnica espelhada do RH/DP — o cadastro é feito lá e desce para cá",
    fonteRh: true,
    ordenarPor: "nome",
    fields: [
      { name: "nome", label: "Nome", type: "text", required: true, origemRh: true },
      { name: "cargo", label: "Cargo", type: "text", origemRh: true },
      {
        name: "setor",
        label: "Setor",
        type: "select",
        // Setores em ordem alfabética, como toda lista de nomes (src/ordem.ts).
        options: ordenarPorNome(
          ["Operações", "Manutenção", "Engenharia", "Ensaios", "Planejamento", "Logística"],
          (s) => s,
        ),
        origemRh: true,
      },
      { name: "skills", label: "Skills / competências", type: "skills", origemRh: true },
      { name: "email", label: "E-mail", type: "email", origemRh: true },
      { name: "telefone", label: "Telefone", type: "text", origemRh: true },
      { name: "status", label: "Status", type: "status", options: ["Ativo", "Inativo", "Afastado"], origemRh: true },
    ],
  },
  // A aba "Usuários" foi REMOVIDA: a criação de logins é centralizada no Portal
  // SERCAMP (lá se define o usuário e quais sistemas ele pode acessar). Criar
  // login aqui gerava conta que o portão de acesso do Portal não reconhecia.
  // O acesso passa a ser sempre pelo card do Portal (SSO). Ver server/auth.ts.
  {
    /* Aba de todos os sistemas: o pedido cai na fila do Desenvolvimento. */
    key: "solicitar_atualizacao",
    label: "Solicitar Atualização",
    icon: "wrench",
    resource: "solicitar_atualizacao",
    subtitle:
      "Marque uma reunião com o Desenvolvimento, peça um ajuste ou avise um erro. O pedido vai direto para quem cuida dos sistemas, com o seu nome e a data.",
    fields: [],
  },
];

/**
 * Catálogo de competências (skills) do colaborador — a lista de marcação do
 * cadastro. Fica em ./skills-catalogo para o SERVIDOR também usar (o espelho
 * do RH/DP precisa saber quais competências são da lista padrão).
 */
export { SKILLS_CATALOGO } from "./skills-catalogo";
